<template>
  <div class="recharge-container">
    <el-card class="box-card">
      <div slot="header" class="clearfix">
        <span>{{ $t('recharge.title') }}</span>
      </div>
      <el-form :model="form" ref="form" :rules="rules" label-width="100px">
        <!-- 用户搜索 -->
        <el-form-item :label="$t('recharge.userSearch')" prop="userId">
          <el-row type="flex" align="middle">
            <el-col :span="18">
              <el-input 
                v-model="searchKey" 
                :placeholder="$t('recharge.inputMemberKey')"
                @keyup.enter.native="searchUser">
                <el-button slot="append" icon="el-icon-search" @click="searchUser"></el-button>
              </el-input>
            </el-col>
          </el-row>
        </el-form-item>
        
        <!-- 已选会员信息 -->
        <div v-if="selectedUser" class="selected-user">
          <el-alert
            :title="$t('recharge.selectedMember')"
            type="success"
            :closable="false">
            <template slot="title">
              {{ $t('recharge.selectedMember') }}：{{selectedUser.xingming}} ({{selectedUser.zhanghao}}) - {{ $t('recharge.currentBalance') }}：¥{{selectedUser.yue}}
            </template>
          </el-alert>
        </div>
        
        <!-- 金额输入框 -->
        <el-form-item :label="$t('recharge.rechargeAmount')" prop="rechargeAmount">
          <el-input-number
            v-model="form.rechargeAmount"
            :min="1"
            :step="10"
            step-strictly
            controls-position="right"
            :placeholder="$t('recharge.inputAmount')">
          </el-input-number>
          <span class="unit"></span>
        </el-form-item>
        
        <!-- 充值描述 -->
        <el-form-item :label="$t('recharge.remark')" prop="remark">
          <el-input
            type="textarea"
            v-model="form.remark"
            :placeholder="$t('recharge.inputRemark')"
            :rows="2">
          </el-input>
        </el-form-item>
        
        <!-- 支付方式选择 -->
        <payment-methods 
          v-model="form.paymentMethod" 
          :methods="[1, 3, 4, 5, 6, 7]"
          :label="$t('payment.paymentMethod')">
        </payment-methods>
        
        <!-- 打印设置 -->
        <el-form-item :label="$t('recharge.receiptSettings')">
          <el-switch
            v-model="printSettings.autoPrint"
            :active-text="$t('recharge.autoPrintReceipt')"
            :inactive-text="$t('recharge.manualPrintReceipt')">
          </el-switch>
          <el-select 
            v-if="printSettings.autoPrint" 
            v-model="printSettings.printerName" 
            :placeholder="$t('recharge.selectPrinter')" 
            style="margin-left: 20px;"
            size="small">
            <el-option
              v-for="printer in printers"
              :key="printer.name"
              :label="printer.name"
              :value="printer.name">
            </el-option>
          </el-select>
        </el-form-item>
        
        <!-- 提交按钮 -->
        <el-form-item>
          <el-button type="primary" @click="handleRecharge" :loading="isSubmitting">{{ $t('recharge.submitRecharge') }}</el-button>
          <el-button @click="resetForm">{{ $t('common.reset') }}</el-button>
        </el-form-item>
      </el-form>
    </el-card>
  </div>
</template>

<script>
import PaymentMethods from '@/components/common/PaymentMethods'
import printService from '@/utils/printService'

export default {
  components: {
    PaymentMethods
  },
  data() {
    return {
      // 搜索关键词
      searchKey: '',
      
      // 已选择的用户
      selectedUser: null,
      
      // 充值表单
      form: {
        userId: null,
        rechargeAmount: 100,
        remark: '',
        paymentMethod: 1 // 默认现金支付
      },
      
      // 表单验证规则 
      rules: {
        userId: [
          { required: true, message: this.$t('recharge.pleaseSelectUser'), trigger: 'change' }
        ],
        rechargeAmount: [
          { required: true, message: this.$t('recharge.inputAmount'), trigger: 'blur' },
          { type: 'number', min: 1, message: this.$t('recharge.amountMustBePositive'), trigger: 'blur' }
        ]
      },
      
      // 提交状态
      isSubmitting: false,
      
      // 打印设置
      printSettings: {
        autoPrint: true,
        printerName: ''
      },
      
      // 可用打印机列表
      printers: [
        { name: 'HP LaserJet (默认)' },
        { name: '收据打印机' },
        { name: 'Epson TM-T88V' }
      ],
      
      // 充值结果
      rechargeResult: null
    };
  },
  methods: {
    // 搜索用户
    searchUser() {
      if (!this.searchKey) {
        this.$message.warning(this.$t('recharge.inputSearchKeyword'));
        return;
      }
      
      this.$http({
        url: '/yonghu/search',
        method: 'get',
        params: {
          keyword: this.searchKey
        }
      }).then(({data}) => {
        if (data && data.code === 0) {
          if (data.data && data.data.length > 0) {
            this.handleUserSelect(data.data[0]);
          } else {
            this.$message.warning(this.$t('recharge.noMemberFound'));
          }
        } else {
          this.$message.error(this.$t('recharge.searchMemberFailed'));
        }
      }).catch(error => {
        console.error(this.$t('recharge.searchMemberError'), error);
        this.$message.error(this.$t('recharge.searchMemberRequestFailed'));
      });
    },
    
    // 处理用户选择
    handleUserSelect(user) {
      this.selectedUser = user;
      this.form.userId = user.id;
    },
    
    // 处理充值请求
    async handleRecharge() {
      this.$refs.form.validate(async (valid) => {
        if (!valid) return;
        
        if (!this.selectedUser) {
          this.$message.warning(this.$t('recharge.pleaseSelectUser'));
          return;
        }
        
        this.isSubmitting = true;
        
        try {
          const response = await this.$http({
            url: 'yonghu/recharge',
            method: 'post',
            data: {
              userId: this.form.userId,
              rechargeAmount: this.form.rechargeAmount,
              remark: this.form.remark,
              paymentMethod: this.form.paymentMethod
            }
          });
          
          if (response.data && response.data.code === 0) {
            // 保存充值结果，用于打印
            this.rechargeResult = {
              id: response.data.orderId || new Date().getTime(),
              customerName: this.selectedUser.xingming,
              zhanghao: this.selectedUser.zhanghao,
              rechargeAmount: this.form.rechargeAmount,
              balance: parseFloat(this.selectedUser.yue) + parseFloat(this.form.rechargeAmount),
              paymentMethod: this.form.paymentMethod,
              operatorName: '管理员'
            };
            
            // 提示成功
            this.$message.success(this.$t('recharge.rechargeSuccess'));
            
            // 是否自动打印
            if (this.printSettings.autoPrint) {
              this.printReceipt();
            }
            
            // 显示收据操作对话框
            this.$confirm(this.$t('recharge.askSendReceipt'), this.$t('recharge.rechargeSuccessTitle'), {
              confirmButtonText: this.$t('recharge.sendReceiptEmail'),
              cancelButtonText: this.$t('common.close') || this.$t('common.cancel'),
              distinguishCancelAndClose: true,
              type: 'success'
            }).then(() => {
              // 发送邮件收据
              this.sendReceiptByEmail();
            }).catch(action => {
              if (action === 'cancel') {
                this.resetForm();
              }
            });
          } else {
            this.$message.error(response.data.msg || this.$t('recharge.rechargeFailed'));
          }
        } catch (error) {
          console.error(this.$t('recharge.rechargeRequestFailed'), error);
          this.$message.error(this.$t('recharge.rechargeRequestFailedRetry'));
        } finally {
          this.isSubmitting = false;
        }
      });
    },
    
    // 打印收据
    printReceipt() {
      if (!this.rechargeResult) return;
      
      try {
        printService.printReceipt(this.rechargeResult, 'recharge');
      } catch (error) {
        console.error(this.$t('recharge.printFailed'), error);
        this.$message.error(this.$t('recharge.printReceiptFailed') + error.message);
      }
    },
    
    // 发送邮件收据
    sendReceiptByEmail() {
      if (!this.rechargeResult) return;
      
      let email = '';
      if (this.selectedUser?.zhanghao) {
        email = `${this.selectedUser.zhanghao}@example.com`;
      }
      
      this.$prompt(this.$t('recharge.enterEmail'), this.$t('recharge.sendReceipt'), {
        confirmButtonText: this.$t('recharge.send'),
        cancelButtonText: this.$t('common.cancel'),
        inputValue: email,
        inputPattern: /[\w!#$%&'*+/=?^_`{|}~-]+(?:\.[\w!#$%&'*+/=?^_`{|}~-]+)*@(?:[\w](?:[\w-]*[\w])?\.)+[\w](?:[\w-]*[\w])?/,
        inputErrorMessage: this.$t('recharge.invalidEmail')
      }).then(({ value }) => {
        printService.sendReceiptByEmail(this.rechargeResult, 'recharge', value)
          .then(response => {
            if (response.code === 0) {
              this.$message.success(this.$t('recharge.receiptSentTo') + value);
              this.resetForm();
            } else {
              this.$message.error(this.$t('recharge.sendEmailFailed') + (response.msg || ''));
            }
          })
          .catch(error => {
            this.$message.error(this.$t('recharge.sendEmailFailed') + error.message);
          });
      }).catch(() => {
        this.resetForm();
      });
    },
    
    // 重置表单
    resetForm() {
      this.$refs.form.resetFields();
      this.selectedUser = null;
      this.searchKey = '';
      this.rechargeResult = null;
      this.form.rechargeAmount = 100;
    }
  }
};
</script>

<style scoped>
.recharge-container {
  max-width: 800px;
  margin: 20px auto;
}

.box-card {
  width: 100%;
}

.selected-user {
  margin-bottom: 20px;
}

.unit {
  margin-left: 5px;
}
</style>
